// 전략과제 현황 — 지표 계산·표시 규칙
//
// 계산은 백엔드(/api/task/summary/strategy)가 기존 누적 현황과 같은 방식으로 한다.
// 여기서는 지표 선택, '해당 없음' 판정, 색·숫자 표기만 다룬다.

import { designTokens } from '@/utils/design-tokens'
import type { StrategySummaryMetrics } from '@/api/generated/models/StrategySummaryMetrics'
import type { StrategySummaryMonthlyItem } from '@/api/generated/models/StrategySummaryMonthlyItem'

export type MetricKey = 'task' | 'item' | 'budget' | 'performance'

export const METRIC_KEYS: MetricKey[] = ['task', 'item', 'budget', 'performance']

/** 워크스페이스 '세부실행과제 표시'가 꺼져 있으면 세부실행과제 지표를 뺀다 */
export const getMetricKeys = (showTasks: boolean): MetricKey[] =>
  showTasks ? METRIC_KEYS : METRIC_KEYS.filter((key) => key !== 'task')

export const METRIC_META: Record<
  MetricKey,
  {
    label: string
    short: string
    unit: string
    doneLabel: string
    naReason: string
  }
> = {
  task: {
    label: '세부실행과제 진행률',
    short: '과제 진행',
    unit: '개',
    doneLabel: '(완료)',
    naReason: '세부실행과제가 없습니다',
  },
  item: {
    label: '수행업무 진행률',
    short: '업무 진행',
    unit: '개',
    doneLabel: '(완료)',
    naReason: '수행업무가 없습니다',
  },
  budget: {
    label: '예산 집행률',
    short: '예산 집행',
    unit: '원',
    doneLabel: '(완료)',
    naReason: '예산이 편성되지 않았습니다',
  },
  performance: {
    label: '성과 달성률',
    short: '성과 달성',
    unit: '개',
    doneLabel: '(달성)',
    naReason: '성과지표가 설정되지 않았습니다',
  },
}

// 화면 공통 색 (시안 기준을 디자인 토큰으로 매핑)
export const strategyColors = {
  text: designTokens.colors.gray[6],
  subText: designTokens.colors.gray[4],
  mutedText: designTokens.colors.gray[3],
  border: designTokens.colors.gray[1],
  dashed: designTokens.colors.gray[2],
  track: designTokens.colors.gray[1],
  surface: designTokens.colors.white,
  naSurface: designTokens.colors.gray[0],
  selected: designTokens.colors.primary.DEFAULT,
  selectedRing: designTokens.colors.info.light,
  link: designTokens.colors.info.DEFAULT,
  monthPast: designTokens.colors.secondary.light,
  // 진한 배경 카드 (사업 선택의 '전체')
  darkSurface: designTokens.colors.primary.dark,
  darkSubText: designTokens.colors.gray[2],
  darkTrack: designTokens.colors.primary.light,
}

/** 90% 이상 파랑, 70~90% 주황, 70% 미만 회색 (inverse: 진한 배경 위에서 쓸 밝은 회색) */
export const getRateColor = (rate: number, inverse = false): string => {
  if (rate >= 90) return designTokens.colors.info.DEFAULT
  if (rate >= 70) return designTokens.colors.warning.dark
  return inverse ? designTokens.colors.gray[2] : designTokens.colors.gray[5]
}

/** 지표 값(%). 해당 없음이면 null — 예산은 편성액이 0 이어도 해당 없음 */
export const getRate = (
  metrics: StrategySummaryMetrics,
  key: MetricKey
): number | null => {
  if (key === 'task') return metrics.task?.count_percentage ?? null
  if (key === 'item') return metrics.task_item.count_percentage ?? null
  if (key === 'budget') {
    const target = metrics.task_budget.total_target_figure
    if (target == null || target <= 0) return null
    return metrics.task_budget.figure_percentage ?? null
  }
  return metrics.task_performance.count_percentage ?? null
}

/** (전체) / (완료·달성) 수치 */
export const getTotals = (
  metrics: StrategySummaryMetrics,
  key: MetricKey
): { total: number; done: number } => {
  if (key === 'task') {
    return {
      total: metrics.task?.total_count ?? 0,
      done: metrics.task?.completed_count ?? 0,
    }
  }
  if (key === 'item') {
    return {
      total: metrics.task_item.total_count ?? 0,
      done: metrics.task_item.completed_count ?? 0,
    }
  }
  if (key === 'budget') {
    return {
      total: metrics.task_budget.total_target_figure ?? 0,
      done: metrics.task_budget.total_done_figure ?? 0,
    }
  }
  return {
    total: metrics.task_performance.total_count ?? 0,
    done: metrics.task_performance.completed_count ?? 0,
  }
}

/** 일부 세부실행과제만 값이 있을 때 근거 개수 안내 (예: "세부실행과제 12개 중 10개 기준") */
export const getCoverageNote = (
  metrics: StrategySummaryMetrics,
  key: MetricKey
): string => {
  if (key === 'task' || key === 'item') return ''
  const all = metrics.task_count ?? 0
  const covered =
    (key === 'budget'
      ? metrics.task_budget.total_count
      : metrics.task_performance.total_count) ?? 0
  return covered > 0 && covered < all
    ? `세부실행과제 ${all}개 중 ${covered}개 기준`
    : ''
}

export const getMonthlyRate = (
  item: StrategySummaryMonthlyItem,
  key: MetricKey
): number | null => {
  if (key === 'task') return item.task_percentage ?? null
  if (key === 'item') return item.task_item_percentage ?? null
  if (key === 'budget') return item.task_budget_percentage ?? null
  return item.task_performance_percentage ?? null
}

/** 사업에 예산·성과가 모두 없으면(기관평가인증 등) 표를 업무 진행률 위주로 줄인다 */
export const isProgressOnly = (metrics: StrategySummaryMetrics): boolean =>
  getRate(metrics, 'budget') == null && getRate(metrics, 'performance') == null

/** 월별 완료 건수 (세부실행과제·수행업무만 — 예산·성과는 건수가 없어 null) */
export const getMonthlyDoneCounts = (
  item: StrategySummaryMonthlyItem,
  key: MetricKey
): { inMonth: number | null; cumulative: number | null } | null => {
  if (key === 'task')
    return {
      inMonth: item.task_completed_count ?? null,
      cumulative: item.task_completed_cumulative ?? null,
    }
  if (key === 'item')
    return {
      inMonth: item.task_item_completed_count ?? null,
      cumulative: item.task_item_completed_cumulative ?? null,
    }
  return null
}

export const formatRate = (rate: number | null): string =>
  rate == null ? '—' : `${rate.toFixed(1)}%`

export const formatNumber = (value: number): string =>
  Math.round(value).toLocaleString('ko-KR')

/** 학년도: 당해 3월 ~ 이듬해 2월 */
export const getCurrentSchoolYear = (today: Date = new Date()): number =>
  today.getMonth() + 1 >= 3 ? today.getFullYear() : today.getFullYear() - 1

export const getSchoolYearRange = (
  year: number
): { startDate: string; endDate: string } => {
  const lastDayOfFeb = new Date(year + 1, 2, 0).getDate()
  return {
    startDate: `${year}-03-01`,
    endDate: `${year + 1}-02-${String(lastDayOfFeb).padStart(2, '0')}`,
  }
}

export const getMonthKey = (date: Date = new Date()): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`

/** 월별 막대에서 강조할 달: 이번 달, 학년도가 끝났으면 마지막 달 */
export const getHighlightMonth = (
  monthly: StrategySummaryMonthlyItem[]
): string => {
  const currentKey = getMonthKey()
  const lastKey = monthly[monthly.length - 1]?.month
  return lastKey && currentKey > lastKey ? lastKey : currentKey
}
